import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, RefreshCw, Loader2, ArrowLeft, LogOut, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { auth as firebaseAuth } from "@/firebase";
import { sendEmailVerification } from "firebase/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RESEND_COOLDOWN_MS = 30000; // 30s cooldown to prevent spam

const VerifyEmailPromptPage = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const [cooldownLeft, setCooldownLeft] = useState(0);

  const startCooldown = () => {
    setJustSent(true);
    setCooldownLeft(Math.floor(RESEND_COOLDOWN_MS / 1000));
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, Math.ceil((RESEND_COOLDOWN_MS - elapsed) / 1000));
      setCooldownLeft(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        setJustSent(false);
      }
    }, 1000);
  };

  const handleResendVerification = async () => {
    if (sending || justSent) return;
    setSending(true);
    try {
      // Manual email/password signups (both our legacy "email" provider AND Firebase
      // email/password "firebase_email" provider) must use the branded AURA email.
      // Google OAuth users continue to use Firebase's native sendEmailVerification.
      const provider = user?.auth_provider;
      const useBrandedEmail = provider === "email" || provider === "firebase_email" || !firebaseAuth.currentUser;
      if (useBrandedEmail && user?.email) {
        const res = await axios.post(`${API}/auth/resend-verification`, { email: user.email });
        if (res.data?.success) {
          // Backend distinguishes "email sent" vs "already verified" vs "google user" via message text
          toast.success(res.data.message || "Verification email sent. Please check your inbox.");
          startCooldown();
        } else {
          toast.error(res.data?.message || "Unable to resend verification email. Please try again.");
        }
      } else {
        const fbUser = firebaseAuth.currentUser;
        if (fbUser) {
          const actionCodeSettings = {
            url: `${window.location.origin}/login`,
            handleCodeInApp: false,
          };
          await sendEmailVerification(fbUser, actionCodeSettings);
          toast.success("Verification email sent. Please check your inbox.");
          startCooldown();
        } else {
          toast.error("Please sign in again to resend verification.");
        }
      }
    } catch (error) {
      if (error?.code === "auth/too-many-requests") {
        toast.error("Too many requests. Please wait a moment.");
      } else {
        toast.error("Unable to resend verification email. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  const handleCheckVerification = async () => {
    setChecking(true);
    try {
      const fbUser = firebaseAuth.currentUser;
      if (fbUser) {
        await fbUser.reload();
        if (fbUser.emailVerified) {
          // Re-exchange token to update backend
          await refreshUser();
          toast.success("Email verified! Redirecting...");
          navigate("/dashboard");
          return;
        }
      }
      toast.info("Email not yet verified. Please click the link in your email.");
    } catch {
      toast.error("Failed to check verification status.");
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-aura-bg flex items-center justify-center px-6 relative">
      <div className="noise-overlay fixed inset-0 pointer-events-none" />
      <div className="grid-bg fixed inset-0 pointer-events-none opacity-30" />
      <div className="absolute inset-0 bg-aura-glow opacity-20" />

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <svg width="48" height="48" viewBox="0 0 100 100" className="glow">
            <defs>
              <radialGradient id="ball-verify" cx="30%" cy="30%">
                <stop offset="0%" stopColor="#444"/>
                <stop offset="100%" stopColor="#000"/>
              </radialGradient>
            </defs>
            <circle cx="50" cy="50" r="45" fill="url(#ball-verify)" />
            <circle cx="50" cy="50" r="18" fill="white"/>
            <text x="50" y="58" textAnchor="middle" fontSize="22" fontWeight="bold" fill="black">8</text>
          </svg>
          <span className="font-heading text-3xl font-bold text-white">AURA</span>
        </Link>

        <div className="card-aura p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-aura-emerald/10 flex items-center justify-center">
            <Mail className="w-8 h-8 text-aura-emerald" />
          </div>

          <h1 className="font-heading text-2xl font-bold text-white mb-2">Verify Your Email</h1>
          <p className="text-aura-text-secondary mb-2">
            We need to verify your email before you can access the dashboard.
          </p>
          {user?.email && (
            <p className="text-aura-emerald font-medium mb-6 break-all">{user.email}</p>
          )}

          <div className="space-y-3 mb-6">
            <Button
              className="w-full btn-primary py-5"
              onClick={handleCheckVerification}
              disabled={checking}
              data-testid="check-verification-btn"
            >
              {checking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  I've Verified My Email
                </>
              )}
            </Button>

            <Button
              variant="outline"
              className="w-full border-white/20 text-white hover:bg-white/5 py-5 disabled:opacity-50"
              onClick={handleResendVerification}
              disabled={sending || justSent}
              data-testid="resend-verification-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : justSent ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2 text-aura-emerald" />
                  Email sent{cooldownLeft > 0 ? ` (${cooldownLeft}s)` : ""}
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Resend Verification Email
                </>
              )}
            </Button>
          </div>

          <div className="pt-4 border-t border-white/10">
            <button
              onClick={handleLogout}
              className="text-sm text-aura-text-muted hover:text-white transition-colors flex items-center gap-2 mx-auto"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Sign out and use a different account
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmailPromptPage;
